import { IssueCaseDoc, issueTaskCategoryMap } from '@gooduncles/gu-app-schema';
import { Tag } from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { issueCaseKindTagColor, normalizeCaseKind } from 'src/constants/issue.constants';

import { scrollToElement } from 'src/utils/common';
import { getCategoryLabel } from 'src/utils/issue/issue-data-util';
import { isIssueCaseEditable } from 'src/utils/issue/issue-util';

import useIssueTasksForCases from 'src/hooks/subscription/useIssueTasksForCases';
import { useScrollToElement } from 'src/hooks/useScrollToElement';

import columnStyles from 'src/atomic-components/atoms/Column/Column.module.scss';

import styles from './IssuePanel.module.scss';

import IssueCaseCard from '../IssueCaseCard/IssueCaseCard';
import IssueBatchActionButtons from './IssueBatchActionButtons';
import IssueColumnHeader from './IssueColumnHeader';
import IssueTaskGroup from './IssueTaskGroup';

interface IssueEditSectionProps {
  issueCases: IssueCaseDoc[];
  parentProducts?: { [productId: string]: number };
  caseColumnRef: React.RefObject<HTMLDivElement>;
}

const IssueEditSection: FC<IssueEditSectionProps> = ({ issueCases, parentProducts, caseColumnRef }) => {
  const { containerRef: taskColumnRef, scrollTo: scrollToTask } = useScrollToElement(50);

  // Case ID들만 추출하여 불필요한 재구독 방지
  const caseIdString = useMemo(() => issueCases.map((issueCase) => issueCase._id).join(','), [issueCases]);
  const { tasksByCase, totalCount } = useIssueTasksForCases(caseIdString);

  // tasksByCase 내의 task들도 registeredAt 기준 오름차순 정렬
  const sortedTasksByCase = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(tasksByCase).map(([caseId, tasks]) => [
          caseId,
          [...tasks].sort((a, b) => a.registeredAt.localeCompare(b.registeredAt)),
        ]),
      ),
    [tasksByCase],
  );

  const scrollToTaskGroup = useCallback(
    (caseId: string) => {
      scrollToTask(`task-group-${caseId}`);
    },
    [scrollToTask],
  );

  const scrollToIssueTask = useCallback(
    (caseId: string, taskId: string) => {
      scrollToTask(`task-card-${caseId}-${taskId}`);
    },
    [scrollToTask],
  );

  const scrollToCase = useCallback(
    (caseId: string) => {
      scrollToElement(`case-card-${caseId}`, caseColumnRef, 50);
    },
    [caseColumnRef],
  );

  return (
    <>
      <div className={columnStyles.column} ref={caseColumnRef}>
        <IssueColumnHeader>
          {issueCases.length > 0 && (
            <>
              <span>{issueCases.length}건</span>
              <div className={styles.categoryNav}>
                {issueCases.map((issueCase) => (
                  <Tag
                    key={issueCase._id}
                    color={issueCaseKindTagColor[normalizeCaseKind(issueCase.caseKind)]}
                    onClick={() => scrollToCase(issueCase._id)}
                  >
                    {getCategoryLabel(issueCase.category)}
                  </Tag>
                ))}
              </div>
            </>
          )}
        </IssueColumnHeader>
        {/* 이슈 케이스 컴포넌트들 */}
        {issueCases.map((issueCase) => (
          <div className={styles.columnContent} key={issueCase._id} id={`case-card-${issueCase._id}`}>
            <IssueCaseCard
              issueCaseDoc={issueCase}
              parentProducts={parentProducts}
              issueTasks={sortedTasksByCase[issueCase._id] ?? []}
              onTitleClick={() => scrollToTaskGroup(issueCase._id)}
              onIssueTaskClick={scrollToIssueTask}
              editable={isIssueCaseEditable(issueCase)}
            />
          </div>
        ))}
      </div>

      <div className={columnStyles.column} ref={taskColumnRef}>
        <IssueColumnHeader>
          {totalCount > 0 && (
            <>
              <span>{totalCount}건</span>
              <div className={styles.categoryNav}>
                {issueCases.map((issueCase) => {
                  const tasks = sortedTasksByCase[issueCase._id];
                  if (!tasks || tasks.length === 0) return null;
                  return (
                    <div key={issueCase._id} className={styles.taskNavGroup}>
                      <span className={styles.taskNavLabel}>{getCategoryLabel(issueCase.category)}:</span>
                      {tasks.map((task) => (
                        <Tag key={task._id} color='blue' onClick={() => scrollToIssueTask(issueCase._id, task._id)}>
                          {issueTaskCategoryMap[task.category]}
                        </Tag>
                      ))}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </IssueColumnHeader>
        {/* 이슈 태스크 컴포넌트들 */}
        {issueCases.map((issueCase) => {
          const tasks = sortedTasksByCase[issueCase._id];
          if (!tasks || tasks.length === 0) return null;
          return <IssueTaskGroup key={issueCase._id} issueCase={issueCase} tasks={tasks} />;
        })}
      </div>

      <IssueBatchActionButtons issueCases={issueCases} tasksByCase={sortedTasksByCase} />
    </>
  );
};

export default IssueEditSection;
